/**
 * Configuration settings for different environments.
 *
 * Values are resolved in two steps:
 *
 * 1. `window.__ENV__`, injected at container start time by `env.js`. This is how
 *    the application is configured when it runs on ORKA: the entrypoint writes
 *    `env.js` from environment variables coming from the ConfigMap, so a single
 *    image can be promoted across environments and pointing the app at a
 *    different Cognito pool or API is a ConfigMap change, not a rebuild.
 *
 * 2. The compiled map below, selected by REACT_APP_ENV at build time. This is
 *    the path used by `npm start` and by the current CloudFront delivery, which
 *    keeps working exactly as before — `env.js` there carries no values.
 *
 * The compiled fallback for an unrecognised REACT_APP_ENV is NOT a safe default:
 * it points the API at localhost but Cognito at the real dev pool. An app that
 * fell back to it would log in against dev and get 401 from the API while
 * looking healthy. That is why docker-entrypoint.sh refuses to start the
 * container when a key is missing instead of writing it empty — on ORKA this
 * fallback is not reached.
 */

const config = {
  production: {
    cognito_userPoolId: 'us-east-1_S2l5JCvp0',
    cognito_clientId: '3muhr58dabsfm7b2d99p59fbkp',
    cognito_hostedUIUrl: 'https://betrusty-auth-prd2.auth.us-east-1.amazoncognito.com',
    apiUrl: 'https://l6q43vqmqb-vpce-0885831eb728f05fb.execute-api.us-east-1.amazonaws.com/prd2'
  },
  staging: {
    cognito_userPoolId: 'us-east-1_HUTDzpRBx',
    cognito_clientId: '1qgqtnf4006fovbn9m8s6bprlq',
    cognito_hostedUIUrl: 'https://betrusty-auth-stg.auth.us-east-1.amazoncognito.com',
    apiUrl: 'https://w9xg5m3av9-vpce-0bc93940c33365e26.execute-api.us-east-1.amazonaws.com/stg'
  },
  default: { // dev
    cognito_userPoolId: 'us-east-1_G8ALxgCry',
    cognito_clientId: '1musspg99uk2j3lp8orsofpk2t',
    cognito_hostedUIUrl: 'https://betrusty-auth-dev.auth.us-east-1.amazoncognito.com',
    apiUrl: 'http://localhost:8000'
  }
}

const environment = config[process.env.REACT_APP_ENV] || config.default

const runtime = (typeof window !== 'undefined' && window.__ENV__) || {}

// A missing key, "", "undefined" or "null" means "not configured" and the compiled
// value wins; a plain `||` would take the last two as real values. ver GMA-3917
const configured = (value) =>
  value !== undefined && value !== null && value !== '' && value !== 'undefined' && value !== 'null'
const resolve = (key, compiled) => (configured(runtime[key]) ? runtime[key] : compiled)

export const userPoolId = resolve('COGNITO_USER_POOL_ID', environment.cognito_userPoolId)
export const clientId = resolve('COGNITO_CLIENT_ID', environment.cognito_clientId)
export const hostedUIUrl = resolve('COGNITO_HOSTED_UI_URL', environment.cognito_hostedUIUrl)
export const apiUrl = resolve('API_URL', environment.apiUrl)
